import { captureException, withScope } from '@sentry/browser';
import { passwordRegex } from 'constants/password';
import {
  apiClient,
  ResetPasswordError,
  type UserApi,
} from 'modules/api-client';
import { Form } from 'modules/common-ui';
import {
  Caption,
  EyeIcon,
  EyeOffIcon,
  FormField,
  Link,
  TextInput,
} from 'modules/design-system';
import { type FormEvent, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { ErrorMessage } from '../../containers/index.css';

type Props = {
  email: string;
  token: string;
  onSuccess: (user: UserApi) => void;
};

const isServerError = (error: Error) =>
  error instanceof ResetPasswordError && error.status >= 500;

const InvalidToken = () => {
  const [t] = useTranslation('user');

  return (
    <Caption>
      <span>{t('user.resetPassword.form.tokenExpired.label')}</span>
      <br />
      <span>{t('user.resetPassword.form.tokenExpired.newRequest')}</span>
      <Link to="/reset-password">
        {t('user.resetPassword.form.tokenExpired.here')}
      </Link>
    </Caption>
  );
};

const PasswordField = ({
  password,
  onPasswordChange,
  label,
  help,
}: {
  password: string;
  onPasswordChange: (value: string) => void;
  label: string;
  help: string;
}) => {
  const theme = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const id = useId();

  return (
    <FormField label={label} htmlFor={id}>
      <TextInput
        fullWidth
        id={id}
        name="password"
        value={password}
        onChange={(e) => onPasswordChange(e.target.value)}
        type={showPassword ? 'text' : 'password'}
        required
        pattern={passwordRegex.source}
        title={help}
        autoFocus
        autoComplete="off"
        icon={{
          component: showPassword ? (
            <EyeOffIcon color={theme.colors.icon.subtle} />
          ) : (
            <EyeIcon />
          ),
          position: 'right',
          onClick: () => setShowPassword(!showPassword),
        }}
      />
    </FormField>
  );
};

export const ResetForm = ({ email, token, onSuccess }: Props) => {
  const [t] = useTranslation('user');
  const [form, setForm] = useState({ password: '', repeatPassword: '' });
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [passwordsNotMatching, setPasswordsNotMatching] = useState(false);
  const theme = useTheme();

  const handlePasswordChange = (value: string) => {
    setForm((f) => ({ ...f, password: value }));
    setPasswordsNotMatching(
      !!form.repeatPassword && value !== form.repeatPassword,
    );
  };

  const handleRepeatPasswordChange = (value: string) => {
    setForm((f) => ({ ...f, repeatPassword: value }));
    setPasswordsNotMatching(!!form.password && value !== form.password);
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (
      !email ||
      !token ||
      !form.password ||
      !form.repeatPassword ||
      passwordsNotMatching ||
      pending
    )
      return;

    setPending(true);
    apiClient.user
      .resetPassword(email.toLowerCase(), form.password, token)
      .then((user) => {
        onSuccess(user);
      })
      .catch((err: Error) => {
        withScope((scope) => {
          scope.setExtra('email', email);
          scope.setLevel(isServerError(err) ? 'error' : 'warning');
          captureException(err);
        });
        setError(err.message);
        setPending(false);
      });
  };

  return (
    <Form
      onSubmit={handleSubmit}
      style={{ width: '100%' }}
      actions={{
        submit: {
          label: pending
            ? t('user.resetPassword.form.submit.loading')
            : t('user.resetPassword.form.submit.label'),
          disabled:
            !form.password ||
            !form.repeatPassword ||
            passwordsNotMatching ||
            pending,
          variant: 'outline-primary',
          buttonProps: { block: true, style: { marginTop: '8px' } },
        },
      }}
    >
      <PasswordField
        password={form.password}
        onPasswordChange={handlePasswordChange}
        label={t('user.resetPassword.form.password')}
        help={t('user.resetPassword.form.help')}
      />
      <PasswordField
        password={form.repeatPassword}
        onPasswordChange={handleRepeatPasswordChange}
        label={t('user.resetPassword.form.repeatPassword')}
        help=""
      />
      {passwordsNotMatching && (
        <Caption color={theme.colors.text.negative}>
          {t('user.resetPassword.form.passwordsNotMatching')}
        </Caption>
      )}
      <Caption>{t('user.resetPassword.form.help')}</Caption>
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </Form>
  );
};

export { InvalidToken };
